// A HEALTH QUESTION LEFT BLANK IS NOT AN ANSWER (2026-10-01).
//
// The all-states compliance audit of 2026-10-01 found it (X1). Every yes/no question on this page drops the browser's
// own `required` on a phone (Form.tsx QuestionnaireItem, since 2023: "the browser doesn't seem to respect the 'required'
// attribute"), and the submit refused only an influence that was not "no", a pregnancy "yes" and a communicable disease
// with no details. So a client signing on a phone, which is nearly every client, could leave a question blank, the
// state's own included (allergies, a bleeding disorder, eaten in the last 4 hours: 22 states force one of them), and
// the signed waiver printed "(N/A)" where the state's rule wants an answer. Chris, that day: "If we know about rules and
// don't implement them then it's something we need to correct, for every state."
//
// So the submit refuses a question the page asked and nobody answered, and takes the client to it (refusal.ts,
// `health_unanswered`, found by the question's own input name). The `required` attribute stays exactly as it was: the
// refusal is the gate, and the attribute never was. The API refuses the same body (app.py HEALTH_QUESTION_UNANSWERED),
// and what it lets a client leave blank is this file's OPTIONAL_HEALTH_QUESTIONS (its tests read this file).
//
// This file is the pure half: which questions this page asks, in the order it draws them, and the first one asked with
// no answer. Form.tsx draws the questions and says the sentence.
//
// A YES WHOSE BOX IS EMPTY IS NOT AN ANSWER EITHER (2026-10-01, TW-0270; the X1 worker's finding the same evening). Seven of
// the ten questions open a box on a yes (DETAILS_BOX, the page's own list, unchanged), and only the communicable disease one
// was refused with its box empty; the other six signed, and the waiver printed "Yes (unknown)" where the client's allergy,
// bleeding disorder, skin condition or metal belonged. So the submit walks the questions in page order once
// (firstHealthGap): a question with no answer is refused at its radios, as X1 has it, and a yes whose box holds nothing but
// spaces is refused at the box (refusal.ts, `health_details`, found by the box's own input name). The API refuses the same
// body (app.py HEALTH_DETAILS_UNANSWERED), and its tests read DETAILS_BOX so the two lists cannot drift apart.
//
// A YES THAT ASKS FOR A PHOTO (2026-10-08, TW-0279 slice 8). Michigan's body art rules bar the work on skin a numbing product
// has touched unless a doctor prescribed it, and keep the prescription with the record (Mich. Admin. Code R 333.13109(5)). So
// every Michigan signing is asked whether a numbing product is on the skin or will be used (`numbingProduct`, the contract's
// `numbing_prescription`), and a yes opens no box: it offers an uploader for a photo of the prescription (PHOTO_ON_YES, the
// body key the contract names as the question's `photo`).
//
// THE PHOTO IS NEVER A GAP (the same evening). As slice 8 first landed, this walk refused a yes without its photo and the API
// refused it too: a client who had filled everything in could not finish, against Chris's standing rule (2026-09-10: "We
// should never have it so if someone signs and fills out everything they can't finish"). The rule binds the technician and
// the record is the shop's, so the client signs with or without the photo, the record says which, and the ARTIST's signature
// waits until the shop adds the photo from its dashboard (the API's prescription.py). The question itself is X1's like every
// other: a yes or a no, never blank. The API's tests read PHOTO_ON_YES against the door's own list (state_forms.question_photos).
//
// THE FIVE A STATE'S RULE LISTS AS ITS OWN QUESTIONS (2026-10-16, TW-0270 slice 4; the all-states audit's M04). Thirteen states'
// rules list diabetes, epilepsy or seizures or fainting or narcolepsy, a heart condition or high blood pressure, a blood thinner
// or an NSAID in the last 24 hours, or a pregnancy in the last 3 months as a question of its own, and the page had them only as
// examples in the optional medical-history box. Each is Georgia's eaten question's shape: asked where the shop's state names it
// (scripts/state-forms.py VOCAB), a yes or a no with no box, never optional, and a yes recorded, never refused. Diabetes posts
// as `diabetesHistory`, because `diabetes` is a cosmetic tattoo's own question, asked on every cosmetic form: there the page
// asks that one and not the state's (Form.tsx askDiabetes).
import { piercingQuestionsFor, type ServiceType } from "./service";
import { cosmeticQuestionsFor, type CosmeticDetails, type CosmeticQuestionKey } from "./cosmetic";

export type HealthQuestion =
  | "influenceOfDrugsOrAlcohol"
  | "pregnantOrNursing"
  | "communicableDisease"
  | "skinConditions"
  | "allergies"
  | "bleedingDisorders"
  | "eatenRecently"
  // 2026-10-08 (TW-0279 slice 8) — Michigan's numbing product (the contract's `numbing_prescription`)
  | "numbingProduct"
  // 2026-10-16 (TW-0270 slice 4) — the five a state's rule can list (the contract's `diabetes`, `seizures_or_fainting`,
  // `heart_or_blood_pressure`, `blood_thinners_recently`, `pregnant_recently`)
  | "diabetesHistory"
  | "seizuresOrFainting"
  | "heartOrBloodPressure"
  | "bloodThinnersRecently"
  | "pregnantRecently"
  // TW-0526 (2026-10-28, M05) — Arkansas's armed services and Hawaii's armed forces, worded questions whose yes opens a box
  // (the contract's `armed_services`, `armed_forces_serial`)
  | "armedServices"
  | "armedForces"
  | "metalAllergy"
  | "keloidScarring"
  | "recentDentalWork"
  // TW-0279 — a cosmetic tattoo's own (cosmetic.ts COSMETIC_QUESTIONS; keloidScarring is the piercing's too)
  | CosmeticQuestionKey;

/** The one question a client may leave blank. "Are you pregnant or nursing?" carries `optional` on the page, and the
 *  signed waiver prints "(N/A)" for it, as it always has. */
export const OPTIONAL_HEALTH_QUESTIONS: HealthQuestion[] = ["pregnantOrNursing"];

/** What decides which questions a page asks: the state's own three (stateForms.ts requirementsFor), and the SERVICE this
 *  visit is, with where (a piercing in or through the mouth is asked about dental work) and which procedures (a cosmetic
 *  tattoo's lips are asked about cold sores). It was a yes/no for a piercing until TW-0279, which a third service could
 *  only have answered as a tattoo. A cosmetic form asks the allergies and bleeding questions whatever its state does (the
 *  proposal's B10 and B4): the caller says so through `allergies` and `bleeding`, which are what the page draws. */
export type HealthAsks = {
  allergies: boolean;
  bleeding: boolean;
  eaten: boolean;
  /** 2026-10-08 (TW-0279 slice 8) — the state's numbing question (Michigan's), asked where the shop's state names
   *  `numbing_prescription`, on every service; absent is not asked */
  numbing?: boolean;
  /** 2026-10-16 (TW-0270 slice 4) — the five a state's rule can list as its own questions, each asked where the shop's state
   *  names its id; absent is not asked. The caller says false for diabetes on a cosmetic form, which asks its own. */
  diabetes?: boolean;
  seizures?: boolean;
  heart?: boolean;
  bloodThinners?: boolean;
  pregnantRecently?: boolean;
  /** TW-0526 (2026-10-28, M04) — the keys of the questions a state's rule lists that the contract carries in their own words
   *  (stateForms.ts wordedQuestionsFor), asked after the five, in the state's order; absent asks none */
  worded?: readonly string[];
  service: ServiceType | "";
  location: string;
  procedures?: readonly string[];
  /** TW-0279 slice 6 — the SHOP's state, for a cosmetic tattoo's state question (cosmetic.ts COSMETIC_STATE_QUESTIONS:
   *  New Jersey's pregnancy bar, California's herpes question, the face question), asked only where that state's record
   *  names it */
  state?: string | null;
  /** TW-0279 slice 9 — the SHOP's row (`public_data`), whose own switch can take a state's question off the page (South
   *  Carolina's physician-directed studio is not asked the face question) */
  publicData?: unknown;
  /** TW-0293 (2026-10-15) — the two questions every form used to ask, which follow the state now (defaultQuestions.ts):
   *  `false` leaves one off the page; absent asks it, as every page did before */
  influence?: boolean;
  communicable?: boolean;
};

/** The health questions this page asks, in the order it draws them: the four every page asked (two of them only where
 *  the shop's state names them or the shop turned them on, TW-0293), the state's own, then the service's own (a
 *  piercing's, or a cosmetic tattoo's). A tattoo, and a page whose client has not picked, has none. */
export function healthQuestionsAsked(asks: HealthAsks): HealthQuestion[] {
  const asked: HealthQuestion[] = [];
  if (asks.influence !== false) asked.push("influenceOfDrugsOrAlcohol");
  asked.push("pregnantOrNursing");
  if (asks.communicable !== false) asked.push("communicableDisease");
  asked.push("skinConditions");
  if (asks.allergies) asked.push("allergies");
  if (asks.bleeding) asked.push("bleedingDisorders");
  if (asks.eaten) asked.push("eatenRecently");
  if (asks.numbing) asked.push("numbingProduct");
  if (asks.diabetes) asked.push("diabetesHistory");
  if (asks.seizures) asked.push("seizuresOrFainting");
  if (asks.heart) asked.push("heartOrBloodPressure");
  if (asks.bloodThinners) asked.push("bloodThinnersRecently");
  if (asks.pregnantRecently) asked.push("pregnantRecently");
  // a worded question's key is the contract's, not one of this file's names: it is refused blank exactly as they are
  asked.push(...((asks.worded ?? []) as readonly string[] as HealthQuestion[]));
  if (asks.service === "piercing") asked.push(...piercingQuestionsFor(asks.location).map((question) => question.name));
  if (asks.service === "cosmetic") {
    asked.push(...cosmeticQuestionsFor(asks.procedures ?? [], asks.state, asks.publicData).map((question) => question.name));
  }
  return asked;
}

/** A yes or a no. The empty string every question starts on is no answer, and neither is anything else. */
export const answered = (value: unknown): boolean => value === "yes" || value === "no";

/** The first question asked that is not optional and has no answer: where the refusal takes the client. "" when every
 *  one is answered. The submit walks firstHealthGap below, which names the same question for every blank. */
export function firstUnanswered(asked: HealthQuestion[], answers: Partial<Record<HealthQuestion, string>>): HealthQuestion | "" {
  return asked.find((key) => !OPTIONAL_HEALTH_QUESTIONS.includes(key) && !answered(answers[key])) ?? "";
}

/** The box a yes opens, by the name it posts under. */
export type DetailsBox =
  | "communicableDiseaseDetails"
  | "skinConditionsDetails"
  | "allergiesDetails"
  | "bleedingDisordersDetails"
  | "armedServicesDetails"
  | "armedForcesDetails"
  | "metalAllergyDetails"
  | "keloidScarringDetails"
  | "recentDentalWorkDetails"
  | CosmeticDetails;

/** Which questions open a box on a yes, and the box: two of the four every page asks, the state's allergies and bleeding
 *  disorder, a piercing's three (service.ts PIERCING_QUESTIONS), and a cosmetic tattoo's own eleven in cosmetic.ts's order
 *  (TW-0279; its keloid question is the piercing's, box and all). The influence and pregnancy questions open none (a yes
 *  to either is refused), and neither does Georgia's eaten question, nor the five a state's rule lists (2026-10-16), nor a cosmetic tattoo's state questions (a yes to the
 *  face question or New Jersey's pregnancy question is refused, one to California's herpes question is recorded). The page
 *  draws exactly these; the API's tests read this list off this file, in this order, against the door's own
 *  (app.health_details_boxes). */
export const DETAILS_BOX: Partial<Record<HealthQuestion, DetailsBox>> = {
  communicableDisease: "communicableDiseaseDetails",
  skinConditions: "skinConditionsDetails",
  allergies: "allergiesDetails",
  bleedingDisorders: "bleedingDisordersDetails",
  armedServices: "armedServicesDetails",
  armedForces: "armedForcesDetails",
  metalAllergy: "metalAllergyDetails",
  keloidScarring: "keloidScarringDetails",
  recentDentalWork: "recentDentalWorkDetails",
  isotretinoin: "isotretinoinDetails",
  diabetes: "diabetesDetails",
  immuneTreatment: "immuneTreatmentDetails",
  autoimmune: "autoimmuneDetails",
  earlierWork: "earlierWorkDetails",
  botoxOrFiller: "botoxOrFillerDetails",
  peelOrLaser: "peelOrLaserDetails",
  coldSores: "coldSoresDetails",
  contactsOrLashes: "contactsOrLashesDetails",
  eyeCondition: "eyeConditionDetails",
  surgeryOrRadiation: "surgeryOrRadiationDetails",
};

/** Something typed in a box: anything but spaces. What was typed is never judged, only whether anything was. */
export const hasDetails = (value: unknown): boolean => typeof value === "string" && value.trim() !== "";

/** The top-level body key a photo a yes asks for is posted under (TW-0279 slice 8). */
export type PhotoOnYes = "photoOfNumbingPrescription";

/** Which questions ask, on a yes, for a PHOTO in place of a box, and the body key it is posted under: Michigan's numbing
 *  product and the doctor's prescription for it. The contract's `photo` on the question's id, the same key (stateForms.test.ts
 *  holds the two together), and the API's tests read this list against the door's own (state_forms.question_photos). */
export const PHOTO_ON_YES: Partial<Record<HealthQuestion, PhotoOnYes>> = {
  numbingProduct: "photoOfNumbingPrescription",
};

/** Where the health answers stop a signing: a question asked and left without a yes or a no (taken to its radios), or a
 *  yes whose box is empty (taken to the box). A yes whose question asks for a PHOTO (PHOTO_ON_YES) is never one: the photo
 *  is the shop's to put on the record, and the client finishes without it (2026-10-08). */
export type HealthGap =
  | { kind: "unanswered"; question: HealthQuestion }
  | { kind: "details"; question: HealthQuestion; box: DetailsBox };

/** The first gap in the health answers, in the order the page draws them, or null when there is none: the optional
 *  question may stay blank, a box is asked for only under a yes, and a photo is never asked for at all. */
export function firstHealthGap(
  asked: HealthQuestion[],
  answers: Partial<Record<HealthQuestion, string>>,
  typed: Partial<Record<DetailsBox, unknown>>
): HealthGap | null {
  for (const question of asked) {
    const answer = answers[question];
    if (!answered(answer)) {
      if (OPTIONAL_HEALTH_QUESTIONS.includes(question)) continue;
      return { kind: "unanswered", question };
    }
    const box = DETAILS_BOX[question];
    if (answer === "yes" && box && !hasDetails(typed[box])) return { kind: "details", question, box };
  }
  return null;
}
